/**
 * Credit health utilities — shared between TrayectoriaSection (cliente individual)
 * y CarteraFunnelSection (agregado de cartera).
 *
 * Funciones puras + helpers visuales (colores, badges, dots de leyenda).
 */

import type { BackofficeCreditsListItem } from '@/types/backoffice-crud.types';

// ─── Score por crédito ────────────────────────────────────────────────────

export function creditScore(credit: BackofficeCreditsListItem): number {
  const status = credit.status?.toUpperCase();
  if (status === 'PAID_OFF' || status === 'SETTLED') return 100;
  if (status === 'DEFAULTED' || status === 'CANCELLED') return 0;
  if (status === 'DELINQUENT') return 35;
  // ACTIVE
  if (!credit.next_due_date) return 90;
  const daysOverdue = -daysUntil(credit.next_due_date);
  if (daysOverdue <= 0) return 95;
  if (daysOverdue <= 7) return 75;
  if (daysOverdue <= 30) return 50;
  return 25;
}

export function scoreColor(score: number): { bar: string; text: string; label: string } {
  if (score >= 90) return { bar: 'bg-green-500', text: 'text-green-500', label: 'puntual' };
  if (score >= 70) return { bar: 'bg-lime-500', text: 'text-lime-500', label: 'tardanzas leves' };
  if (score >= 40) return { bar: 'bg-yellow-500', text: 'text-yellow-500', label: 'tardanzas frecuentes' };
  if (score >= 20) return { bar: 'bg-orange-500', text: 'text-orange-500', label: 'tardanzas graves' };
  return { bar: 'bg-red-500', text: 'text-red-500', label: 'default' };
}

export function statusBadge(status: string): { className: string; label: string } {
  const s = status?.toUpperCase();
  if (s === 'PAID_OFF' || s === 'SETTLED') return { className: 'bg-green-900/40 text-green-400', label: 'Liquidado' };
  if (s === 'ACTIVE') return { className: 'bg-blue-900/40 text-blue-400', label: 'Activo' };
  if (s === 'DELINQUENT') return { className: 'bg-yellow-900/40 text-yellow-400', label: 'Delincuente' };
  if (s === 'DEFAULTED') return { className: 'bg-red-900/40 text-red-400', label: 'Default' };
  if (s === 'CANCELLED') return { className: 'bg-red-900/40 text-red-400', label: 'Cancelado' };
  return { className: 'bg-muted text-muted-foreground', label: s };
}

// ─── Date helpers ─────────────────────────────────────────────────────────

export function daysUntil(iso: string): number {
  if (!iso) return 0;
  const target = new Date(iso).getTime();
  const now = Date.now();
  return Math.floor((target - now) / (1000 * 60 * 60 * 24));
}

// ─── Cobro: vencido vs futuro ─────────────────────────────────────────────

export function computeOverdueAmount(credit: BackofficeCreditsListItem): number {
  const status = credit.status?.toUpperCase();
  if (status === 'PAID_OFF' || status === 'SETTLED') return 0;

  const weeksRemaining = (credit.weeks_total ?? credit.term_weeks ?? 0) - (credit.weeks_paid ?? 0);
  const remainingAmount = Math.max(0, weeksRemaining * (credit.weekly_payment_mxn ?? 0));

  if (status === 'DEFAULTED' || status === 'CANCELLED') return remainingAmount;

  // ACTIVE / DELINQUENT
  if (!credit.next_due_date) return 0;
  const days = daysUntil(credit.next_due_date);
  if (days >= 0) return 0;
  const weeksOverdue = Math.min(weeksRemaining, Math.ceil(-days / 7));
  return Math.max(0, weeksOverdue * (credit.weekly_payment_mxn ?? 0));
}

// ─── Salud global (promedio ponderado por monto) ──────────────────────────

export function computeHealthGlobal(credits: BackofficeCreditsListItem[]): number {
  if (credits.length === 0) return 0;
  const totalAmount = credits.reduce((s, c) => s + (c.amount_mxn ?? 0), 0);
  if (totalAmount <= 0) return 0;
  const weighted = credits.reduce((s, c) => s + creditScore(c) * (c.amount_mxn ?? 0), 0);
  return weighted / totalAmount;
}

// ─── Tendencia (delta del último crédito vs promedio anterior) ────────────

export type Tendency = { dir: 'up' | 'down' | 'flat'; delta: number } | null;

export function computeTendency(credits: BackofficeCreditsListItem[]): Tendency {
  if (credits.length < 2) return null;
  const sorted = [...credits].sort((a, b) => (a.disbursed_at ?? '').localeCompare(b.disbursed_at ?? ''));
  const last = sorted[sorted.length - 1];
  if (!last) return null;
  const lastScore = creditScore(last);
  const prev = sorted.slice(0, -1);
  const prevAvg = prev.reduce((s, c) => s + creditScore(c), 0) / prev.length;
  const delta = Math.round(lastScore - prevAvg);
  if (Math.abs(delta) < 5) return { dir: 'flat', delta: 0 };
  return delta > 0 ? { dir: 'up', delta } : { dir: 'down', delta: Math.abs(delta) };
}

// ─── Componente: leyenda compacta ─────────────────────────────────────────

export function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`inline-block h-2 w-2 rounded-sm ${color}`} />
      <span>{label}</span>
    </span>
  );
}
